import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Target, Flame, Award, Heart, DollarSign, Sparkles, Brain, Smile, Eye, Loader, Menu } from 'lucide-react';
import { getCurrentUser } from '../shared/api/auth';
import { fetchCompletedSessions } from '../shared/api/sessions';

// ✅ Import centralized theme
import { gradients, buttonStyles, cardStyles, avatarStyles } from '../shared/config/theme';
import { getJourneyConfig } from '../shared/constants/journeyConfig';
import { getTimeGreeting } from '../shared/utils/dateHelpers';
// ✅ Import centralized components
import Header from '../shared/components/Header';



const ProgressDashboard = ({ user, userProfile, onNavigate,isPremium,streakCount  }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [journeyStats, setJourneyStats] = useState({});
  const [thisMonthCount, setThisMonthCount] = useState(0);
  const [weeklyData, setWeeklyData] = useState([]);

  useEffect(() => {
    fetchSessionsData();
  }, []);

  useEffect(() => {
    if (sessions.length > 0) {
      calculateStats();
    }
  }, [sessions]);

 // ✅ Navigation handlers for Header component
  const handleMenuClick = () => {
    console.log('Menu clicked - navigating to more');
    if (onNavigate) {
      onNavigate('more');
    } else {
      console.error('onNavigate function not provided');
    }
  };

  const handleProgressClick = () => {
    console.log('Progress clicked - navigating to progress');
    if (onNavigate) {
      onNavigate('progress');
    } else {
      console.error('onNavigate function not provided');
    }
  };

  const fetchSessionsData = async () => {
    try {
      setLoading(true);
      const { user: currentUser, error: userError } = await getCurrentUser();
      if (userError || !currentUser) {
        setLoading(false);
        return;
      }

      const { sessions: fetchedSessions, error } = await fetchCompletedSessions(currentUser.id);

      if (error) throw error;

      const transformed = fetchedSessions.map(session => ({
        id: session.id,
        journey: session.journeys?.category || 'confidence',
        completed_at: (session.completed_at || session.created_at).split('T')[0],
        status: 'complete'
      }));

      setSessions(transformed);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setLoading(false);
    }
  };

  const getUserName = () => {
    if (userProfile?.full_name) return userProfile.full_name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    return 'Friend';
  };

  const getUserInitial = () => {
    const name = getUserName();
    return name.charAt(0).toUpperCase();
  };

  const calculateStats = () => {
    if (sessions.length === 0) {
      setCurrentStreak(0);
      setLongestStreak(0);
      setJourneyStats({});
      setThisMonthCount(0);
      setWeeklyData([]);
      return;
    }

    const uniqueDates = [...new Set(sessions.map(s => s.completed_at))].sort((a, b) => 
      new Date(b) - new Date(a)
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let streak = 0;
    
    for (let i = 0; i < uniqueDates.length; i++) {
      const sessionDate = new Date(uniqueDates[i]);
      sessionDate.setHours(0, 0, 0, 0);
      const expectedDate = new Date(today);
      expectedDate.setDate(expectedDate.getDate() - i);
      expectedDate.setHours(0, 0, 0, 0);
      
      if (sessionDate.getTime() === expectedDate.getTime()) {
        streak++;
      } else {
        break;
      }
    }
    
    setCurrentStreak(streak);
    
    let longest = 0;
    let tempStreak = 0;
    
    for (let i = 0; i < uniqueDates.length; i++) {
      if (i === 0) {
        tempStreak = 1;
      } else {
        const currentDate = new Date(uniqueDates[i]);
        const previousDate = new Date(uniqueDates[i - 1]);
        const dayDiff = Math.floor((previousDate - currentDate) / (1000 * 60 * 60 * 24));
        
        if (dayDiff === 1) {
          tempStreak++;
        } else {
          longest = Math.max(longest, tempStreak);
          tempStreak = 1;
        }
      }
    }
    longest = Math.max(longest, tempStreak);
    setLongestStreak(longest);
    
    const stats = sessions.reduce((acc, session) => {
      const journey = session.journey || 'unknown';
      acc[journey] = (acc[journey] || 0) + 1;
      return acc;
    }, {});
    setJourneyStats(stats);

    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const monthCount = sessions.filter(s => {
      const sessionDate = new Date(s.completed_at);
      return sessionDate.getMonth() === currentMonth && sessionDate.getFullYear() === currentYear;
    }).length;
    setThisMonthCount(monthCount);

    const weeks = [];
    for (let i = 3; i >= 0; i--) {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - (i * 7) - today.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const weekSessions = sessions.filter(s => {
        const sessionDate = new Date(s.completed_at);
        return sessionDate >= weekStart && sessionDate <= weekEnd;
      });
      
      weeks.push({
        week: `Week ${4 - i}`,
        sessions: weekSessions.length
      });
    }
    setWeeklyData(weeks);
  };

  const getCalendarData = () => {
    const last30Days = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const hasSession = sessions.some(s => s.completed_at === dateStr);
      last30Days.push({ date: dateStr, hasSession, day: date.getDate() });
    }
    
    return last30Days;
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${gradients.background}`}>
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  const calendarData = getCalendarData();
  const maxWeekly = Math.max(...weeklyData.map(w => w.sessions), 1);
  const currentMonthName = new Date().toLocaleDateString('en-US', { month: 'long' });

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradients.background}`}>
      {/* Header */}
       {/* ✅ Centralized Header Component with Fixed Navigation */}
      <Header
        user={user}
        userProfile={userProfile}
        isPremium={isPremium}
        streakCount={streakCount}
        onMenuClick={handleMenuClick}
        onProgressClick={handleProgressClick}
        showProgress={!!user}
        subtitle="Reflect on your journey"
      />

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-orange-100 to-peach-100 rounded-xl">
                <Flame className="w-6 h-6 text-orange-500" />
              </div>
              <span className="text-3xl font-bold text-orange-500">{currentStreak}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Current Streak</h3>
            <p className="text-xs text-gray-500 mt-1">days in a row</p>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-peach-100 to-cream-100 rounded-xl">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-3xl font-bold text-orange-600">{sessions.length}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Total Sessions</h3>
            <p className="text-xs text-gray-500 mt-1">completed</p>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-cream-100 to-peach-100 rounded-xl">
                <Award className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-3xl font-bold text-orange-600">{longestStreak}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Longest Streak</h3>
            <p className="text-xs text-gray-500 mt-1">personal best</p>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-orange-100 to-cream-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-3xl font-bold text-orange-600">{thisMonthCount}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">This Month</h3>
            <p className="text-xs text-gray-500 mt-1">{currentMonthName}</p>
          </div>
        </div>

        {/* Calendar & Journey Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className={`lg:col-span-2 ${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
            <div className="flex items-center mb-6">
              <Calendar className="w-5 h-5 text-orange-600 mr-2" />
              <h2 className="text-xl font-bold text-gray-800">Activity Calendar</h2>
            </div>
            
            <div className="grid grid-cols-10 gap-2">
              {calendarData.map((day, idx) => (
                <div
                  key={idx}
                  className={`aspect-square rounded-lg ${
                    day.hasSession 
                      ? 'bg-gradient-to-br from-orange-400 to-orange-500' 
                      : 'bg-gray-100'
                  } hover:scale-110 transition-transform cursor-pointer relative group`}
                  title={day.date}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-xs font-medium ${
                      day.hasSession ? 'text-white' : 'text-gray-400'
                    }`}>
                      {day.day}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex items-center justify-end mt-4 gap-2 text-xs text-gray-600">
              <span>Less</span>
              <div className="w-4 h-4 bg-gray-100 rounded"></div>
              <div className="w-4 h-4 bg-orange-300 rounded"></div>
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
              <span>More</span>
            </div>
          </div>

          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
            <div className="flex items-center mb-6">
              <Brain className="w-5 h-5 text-orange-600 mr-2" />
              <h2 className="text-xl font-bold text-gray-800">Journey Stats</h2>
            </div>
            
            {Object.keys(journeyStats).length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">
                No sessions completed yet. Start your first journey!
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(journeyStats).map(([journey, count]) => {
                  // ✅ Use centralized journey config
                  const config = getJourneyConfig(journey);
                  const Icon = config.icon;
                  const percentage = Math.round((count / sessions.length) * 100);
                  
                  return (
                    <div key={journey} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 ${config.lightColor} rounded-lg`}>
                            <Icon className={`w-4 h-4 ${config.textColor}`} />
                          </div>
                          <span className="text-sm font-medium text-gray-700 capitalize">
                            {journey}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">{count}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className={`bg-gradient-to-r ${config.gradient} h-2 rounded-full transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Weekly Progress Chart */}
        <div className={`mt-8 ${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.border} ${cardStyles.default.padding}`}>
          <div className="flex items-center mb-6">
            <TrendingUp className="w-5 h-5 text-orange-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-800">Weekly Progress</h2>
          </div>
          
          {weeklyData.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">
              Complete sessions to see your weekly progress
            </p>
          ) : (
            <div className="flex items-end justify-between gap-4 h-48">
              {weeklyData.map((week) => (
                <div key={week.week} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full flex items-end justify-center h-full">
                    <div
                      className={`w-full bg-gradient-to-t ${gradients.button} rounded-t-lg transition-all duration-500 hover:opacity-90`}
                      style={{ height: `${(week.sessions / maxWeekly) * 100}%`, minHeight: week.sessions > 0 ? '20%' : '0%' }}
                    >
                      {week.sessions > 0 && (
                        <div className="w-full h-full flex items-start justify-center pt-2">
                          <span className="text-white font-bold text-sm">{week.sessions}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-600">{week.week}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Encouragement Card */}
        <div className={`mt-8 bg-gradient-to-r ${gradients.button} rounded-3xl p-8 text-white shadow-xl`}>
          <h3 className="text-2xl font-bold mb-2">🌟 You're doing amazing!</h3>
          <p className="text-orange-50">
            {currentStreak > 0 
              ? `Keep your ${currentStreak}-day streak going! You're building powerful manifestation habits.`
              : sessions.length > 0
              ? "Start a new streak today! Consistency is the key to manifestation."
              : "Start your journey today! Every manifestation begins with a single step."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressDashboard;